<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Assessments
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`View all assessments.`">info</i>
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-quiz' }">
          Add Assessment
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center justify-content-between" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-2" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Assessments"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto d-flex" style="gap: 12px;">
                <div class="select-with-caret" style="width: 220px; margin-bottom: 6px;">
                  <b-form-select
                    class="select-with-caret__control"
                    v-model="examFilter"
                    :options="examFilterOptions"
                    @change="onFilterChange"
                    style="border-color: #e0e0e0; height: 38px;"
                  >
                  </b-form-select>
                  <md-icon class="select-with-caret__icon">expand_more</md-icon>
                </div>
                <v-select
                  class="form-control v-select-custom"
                  style="width: 220px; height: 38px; margin-bottom: 6px;"
                  label="title"
                  v-model="selectedCourse"
                  :reduce="course => course.id"
                  placeholder="Filter by Course"
                  :options="courses"
                  :loading="areCoursesLoading"
                  @input="onCourseFilter"
                  :clearable="true"
                >
                </v-select>
                <v-select
                  class="form-control v-select-custom"
                  style="width: 220px; height: 38px; margin-bottom: 6px;"
                  label="title"
                  v-model="selectedProgram"
                  :reduce="program => program.id"
                  placeholder="Filter by Program"
                  :options="allPrograms"
                  :loading="areProgramsLoading"
                  @input="onProgramFilter"
                  :clearable="true"
                >
                </v-select>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="quizzes"
              v-if="quizzes.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(title)="data">
                <strong>{{ data.item.title }}</strong>
                <b-badge v-if="data.item.is_mock_exam" variant="info" class="ml-1">Mock</b-badge>
              </template>

              <template #cell(is_exam)="data">
                <md-icon v-if="data.item.is_exam" class="text-success">done</md-icon>
                <md-icon v-else class="text-danger">close</md-icon>
              </template>

              <template #cell(max_attempts)="data">
                {{ data.item.max_attempts || 'Unlimited' }}
              </template>

              <template #cell(duration_minutes)="data">
                {{
                  data.item.duration_minutes ? formatMinutesToHoursAndMinutes(data.item.duration_minutes) : 'No limit'
                }}
              </template>

              <template #cell(total_marks)="data">
                {{ data.item.total_marks }}
              </template>

              <template #cell(passing_percentage)="data"> {{ data.item.passing_percentage }}% </template>

              <template #cell(program)="data">
                {{ data.item.program ? data.item.program.title : '-' }}
              </template>

              <template #cell(weightage)="data">
                <span v-if="data.item.weightage !== undefined"> {{ data.item.weightage }}% </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(actions)="data">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                  <template #button-content>
                    <i class="material-icons icon-24pt text-info">more_vert</i>
                  </template>
                  <b-dropdown-item
                    :to="{
                      name: 'edit-quiz',
                      params: { id: data.item.id },
                    }"
                  >
                    <md-icon class="text-info mr-2">edit</md-icon>
                    {{ $t('generalMsgs.edit') }}
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click="confirmDeleteQuiz(data.item.id)">
                    <md-icon class="text-danger mr-2">delete</md-icon>
                    {{ $t('generalMsgs.remove') }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalQuizzes"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="quizzes-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
// import { formatFullDate } from '@/common/utils';
import { formatMinutesToHoursAndMinutes } from '@/common/utils';
import Pagination from '../../../components/Ui/Pagination.vue';
import persistListFilters from '@/mixins/persist-list-filters';

export default {
  components: {
    Pagination,
  },
  extends: Page,
  mixins: [persistListFilters],
  filterKeys: ['searchTerm', 'examFilter', 'selectedCourse', 'selectedProgram', 'currentPage'],

  data() {
    return {
      title: 'View Quizzes',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalQuizzes: 0,
      searchTerm: null,
      quizzes: [],
      limit: 15,
      offset: 0,
      examFilter: null,
      examFilterOptions: [
        { value: null, text: 'All Assessments' },
        { value: 'true', text: 'Final Exams' },
        { value: 'false', text: 'Assessments' },
        { value: 'mock', text: 'Mock Exams' },
      ],
      allPrograms: [],
      areProgramsLoading: false,
      selectedProgram: null,
      courses: [],
      areCoursesLoading: false,
      selectedCourse: null,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          tdClass: 'text-left',
          thStyle: { minWidth: '100px' },
        },
        { key: 'title', label: 'Title', sortable: true, tdClass: 'clickable-item', thStyle: { minWidth: '200px' } },
        { key: 'program', label: 'Program', sortable: false, thStyle: { minWidth: '150px' } },
        { key: 'is_exam', label: 'Final Exam', class: 'text-center' },
        { key: 'weightage', label: 'Weightage', class: 'text-center' },
        { key: 'max_attempts', label: 'Max Attempts', class: 'text-center' },
        { key: 'duration_minutes', label: 'Duration', class: 'text-center' },
        { key: 'total_marks', label: 'Total Marks', class: 'text-center' },
        { key: 'passing_percentage', label: 'Passing %', class: 'text-center' },
        // {
        //   key: 'created_at',
        //   label: 'Created At',
        //   formatter: value => (value ? formatFullDate(value) : value),
        //   sortable: true,
        // },
        // {
        //   key: 'updated_at',
        //   label: 'Updated At',
        //   formatter: value => (value ? formatFullDate(value) : value),
        //   sortable: true,
        // },
      ];
    },
  },

  methods: {
    get,
    formatMinutesToHoursAndMinutes,
    ...mapActions('quizzes', ['getQuizzes', 'deleteQuiz']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('courses', ['getLmsCourses']),

    onPageChange(pageNum) {
      this.fetchAllQuizzes(pageNum);
    },

    async confirmDeleteQuiz(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this quiz?', {
          title: 'Delete Quiz',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          cancelVariant: 'secondary',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.onDeleteQuiz(id);
          this.fetchAllQuizzes();
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onDeleteQuiz(id) {
      try {
        await this.deleteQuiz(id);
        this.makeToast({ variant: 'success', msg: 'Quiz successfully deleted' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    async fetchAllQuizzes(params = {}) {
      this.isLoading = true;
      try {
        const res = await this.getQuizzes({
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.examFilter === 'mock' ? { is_mock_exam: true } : this.examFilter !== null ? { is_exam: this.examFilter } : {}),
          ...(this.selectedProgram && { program: this.selectedProgram }),
          ...(this.selectedCourse && { course: this.selectedCourse }),
          ...params,
        });
        const responseData = res.data || {};
        this.quizzes = responseData.results ? responseData.results : responseData;
        this.totalQuizzes = responseData.count || (Array.isArray(this.quizzes) ? this.quizzes.length : 0);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onSortChange(context) {
      const ordering = context.sortDesc ? `-${context.sortBy}` : context.sortBy;
      this.fetchAllQuizzes({ ordering });
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedQuizSearch(this);
    },

    onFilterChange() {
      this.currentPage = 1;
      this.fetchAllQuizzes();
    },
    onProgramFilter() {
      this.currentPage = 1;
      this.fetchAllQuizzes();
    },
    onCourseFilter() {
      this.currentPage = 1;
      this.fetchAllQuizzes();
    },

    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const response = await this.getLmsCourses({
          school__id: this.getLoggedInUser.linked_entity.id,
          page_size: 100,
          ordering: 'title',
        });
        this.courses = response.data || [];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areCoursesLoading = false;
    },

    debouncedQuizSearch: debounce(vm => {
      vm.fetchAllQuizzes();
    }, 500),
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchCourses();
    await this.fetchAllQuizzes();
  },
};
</script>

<style scoped lang="scss">
.loader {
  text-align: center;
  color: #bbbbbb;
  margin-inline: auto;
}
.select-with-caret {
  position: relative;
}
.select-with-caret__control::after {
  display: none !important;
}
.select-with-caret__icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #757575;
  font-size: 20px;
}
</style>
